/**
 * RegionIndicator Component
 *
 * Displays the current region name as an overlay at the top of the map view.
 * Shows the wall section the user is currently viewing based on viewport center.
 *
 * Features:
 * - Semi-transparent pill background
 * - Smooth fade in/out transitions
 * - Positioned at top-center of the map
 *
 * @example
 * ```tsx
 * <RegionIndicator
 *   regionName="Main Wall"
 *   visible={viewMode === 'pins'}
 * />
 * ```
 */

import { memo, type CSSProperties } from 'react';

/**
 * Props for RegionIndicator component
 */
export interface RegionIndicatorProps {
  /** Current region name to display, or null if outside all regions */
  regionName: string | null;
  /** Whether the indicator is visible */
  visible: boolean;
}

/**
 * RegionIndicator Component
 *
 * Shows the current wall section name as an overlay pill at the top of the map.
 */
const RegionIndicatorBase = ({ regionName, visible }: RegionIndicatorProps) => {
  // Don't render if no region or not visible
  if (!visible || !regionName) {
    return null;
  }

  return (
    <div style={containerStyle}>
      <div style={pillStyle}>{regionName}</div>
    </div>
  );
};

// Styles
const containerStyle: CSSProperties = {
  position: 'absolute',
  top: '12px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10,
  pointerEvents: 'none',
};

const pillStyle: CSSProperties = {
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: '#ffffff',
  padding: '6px 16px',
  borderRadius: '20px',
  fontSize: '14px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
};

// Memoize to prevent unnecessary re-renders
export const RegionIndicator = memo(RegionIndicatorBase);

export default RegionIndicator;
